import { computed } from 'vue'
import ActivityPhotosField from '../../components/work-order-details/ActivityPhotosField.vue'

const TRANSLATION_NAMESPACE = 'road-maintenance/temporary_regulation/work_order_details'
const COLUMNS_COUNT = 4

export function useActivityFields (options) {
  const { translate, activity: getActivity } = options || {}

  const fields = computed(() => {
    // Called (not just read) inside this computed so it's evaluated fresh on
    // every recompute, tracking whichever activity object is CURRENTLY
    // assigned to the prop - a plain reference captured once at setup time
    // would go stale the moment the location's `activities` array gets
    // replaced wholesale (eg. after "refresh data" re-fetches everything),
    // silently freezing `edit` at whatever it was and breaking the fields
    // that should hide while editing.
    const activity = typeof getActivity === 'function' ? getActivity() : getActivity

    const allFields = [
      {
        key: 'activity_id',
        label: translate(`${TRANSLATION_NAMESPACE}.activity`),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'road-maintenance',
          route: 'activities/autocomplete'
        },
        editable: true,
        visible: true
      },
      {
        // Read-only display of an already formatted datetime string - a plain
        // text field, not a picker, since there is nothing to pick here.
        key: 'actual_start',
        label: translate(`${TRANSLATION_NAMESPACE}.actual_start`),
        editable: false,
        visible: (vm) => !!vm.formData?.id && !vm.edit
      },
      {
        key: 'actual_end',
        label: translate(`${TRANSLATION_NAMESPACE}.actual_end`),
        editable: false,
        visible: (vm) => !!vm.formData?.id && !vm.edit
      },
      {
        key: 'status_label',
        label: translate(`${TRANSLATION_NAMESPACE}.status`),
        editable: false,
        visible: (vm) => !!vm.formData?.id && !vm.edit
      },
      {
        key: 'photos',
        type: 'custom',
        component: ActivityPhotosField,
        label: translate(`${TRANSLATION_NAMESPACE}.photos`),
        editable: false,
        // Purely informational - an activity with no uploaded photos has
        // nothing to show, so the field (and its empty box) stays hidden.
        visible: (vm) => !!vm.formData?.id && !vm.edit && !!vm.formData?.photos?.length
      },
      {
        key: 'instructions',
        label: translate(`${TRANSLATION_NAMESPACE}.instructions`),
        type: 'textarea',
        editable: true,
        visible: true
      }
    ]

    // FieldsRenderer groups fields by their `row` and pads each row up to
    // `columnsCount` with blank placeholders - fixed row numbers left
    // `activity_id` alone (with 3 blank slots) whenever the other fields on
    // its row were hidden (eg. entering edit mode). Assign rows dynamically,
    // after filtering to only the currently visible fields, so they always
    // pack 4-per-row with no gaps.
    const evalVm = { formData: activity, edit: !!activity?.editing }

    return allFields
      .filter(field => {
        const { visible } = field
        return typeof visible === 'function' ? visible(evalVm) : !!visible
      })
      .map((field, index) => ({
        ...field,
        row: Math.floor(index / COLUMNS_COUNT),
        visible: true
      }))
  })

  return computed(() => ({
    columnsCount: 4,
    fields: fields.value
  }))
}
